import * as yup from "yup";
import {useFormik} from "formik";
import {Button, Label, Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import LabelInput from "../../../components/Common/LabelInput";
import React, {ChangeEvent, useEffect, useMemo, useState} from "react";
import {IErrorClassifierGroupForm} from "../../../store/errorClassifier/types";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {ErrorClassifierService} from "../../../services/error_classifier.service";
import NoSearchResult from "../../../components/Common/NoSearchResult";

interface ModalErrorClassifierGroupProps {
    isOpen: boolean;
    groupId: number | null;
    onClose: () => void;
}

function generateRandomColor() {
    return "#" + Math.floor(Math.random() * (1 << 3 * 8)).toString(16).padStart(6, "0");
}

const ModalErrorClassifierGroup: React.FC<ModalErrorClassifierGroupProps> = ({isOpen, groupId, onClose}) => {
    const isEditModal = groupId !== null;
    const queryClient = useQueryClient();

    const [savedGroup, setSavedGroup] = useState<boolean>(false);
    const [removedGroup, setRemovedGroup] = useState<boolean>(false);
    const [sureToRemove, setSureToRemove] = useState<boolean>(false);

    const {
        data: group,
        isFetching
    } = useQuery({
        queryKey: ["keyErrorClassifierGroup", groupId],
        queryFn: () => ErrorClassifierService.getGroupById(groupId || -1),
        enabled: isEditModal,
    });

    const onSuccess = () => {
        queryClient.invalidateQueries({queryKey: ["keyErrorClassifierGroups"]})
            .then(() => onClose())
    }

    const {mutate: createGroup, isPending: isPendingCreate} = useMutation({
        mutationFn: (data: IErrorClassifierGroupForm) => ErrorClassifierService.createGroup(data),
        onSuccess: onSuccess,
        mutationKey: ["keyCreateErrorClassifierGroup"],
    });

    const {mutate: updateGroup, isPending: isPendingUpdate} = useMutation({
        mutationFn: (data: IErrorClassifierGroupForm) => ErrorClassifierService.updateGroup(groupId!, data),
        onSuccess: onSuccess,
        mutationKey: ["keyUpdateErrorClassifierGroup", groupId],
    });

    const {mutate: removeGroup, isPending: isPendingRemove} = useMutation({
        mutationFn: () => ErrorClassifierService.removeGroup(groupId!),
        onSuccess: onSuccess,
        mutationKey: ["keyRemoveErrorClassifierGroup", groupId],
    });

    const validationSchema: yup.Schema<IErrorClassifierGroupForm> = yup.object().shape({
        name: yup.string()
            .min(1, "min 1 character")
            .max(75, "max 75 characters")
            .required("Group name is required"),

        color: yup.string()
            .required("Color is required")
    });

    const randomColor = useMemo(() => generateRandomColor(), [])
    const initialValues: IErrorClassifierGroupForm = {
        name: group?.name || "",
        color: group?.color || randomColor
    }

    const handleSubmitForm = (result: IErrorClassifierGroupForm) => {
        setSavedGroup(true);

        if (!isEditModal) createGroup(result);
        else updateGroup(result);
    };

    const {
        values,
        handleChange,
        handleSubmit,
        handleBlur,
        setFieldValue,
        errors,
        touched,
        isValid,
        resetForm,
    } = useFormik({
        initialValues: initialValues,
        onSubmit: handleSubmitForm,
        validationSchema: validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true
    });

    const closeModal = () => {
        onClose();
        resetForm();
    };

    const handleColor = (event: ChangeEvent<HTMLInputElement>) => {
        setFieldValue("color", event.currentTarget.value);
    }

    const handleRemoveClick = () => {
        if (!sureToRemove) {
            setSureToRemove(true)
        } else {
            setSureToRemove(false)
            setRemovedGroup(true)
            removeGroup()
        }
    }

    useEffect(() => {
        if (sureToRemove) {
            const timer = setTimeout(() => setSureToRemove(false), 2500);
            return () => clearTimeout(timer);
        }
    }, [sureToRemove]);

    const isPendingSave = isPendingCreate || isPendingUpdate || (!!queryClient.isFetching() && savedGroup);
    const isPendingDelete = isPendingRemove || (!!queryClient.isFetching() && removedGroup);

    return (
        <Modal isOpen={isOpen} toggle={closeModal}>
            <ModalHeader toggle={closeModal} tag="h4">
                {isEditModal ? "Edit" : "Add"} Group
            </ModalHeader>
            {isFetching ? <div className="my-4"><NoSearchResult text="Loading..."/></div> : (
                <form onSubmit={handleSubmit} className="needs-validation">
                    <ModalBody>
                        <div className="mb-3">
                            <LabelInput
                                label="Name*"
                                placeholder="Name*"
                                name="name"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.name}
                                hasError={!!(errors.name && touched.name)}
                                errorText={errors.name}
                            />
                        </div>

                        <div>
                            <Label htmlFor="color">Color: </Label>
                            <input type="color"
                                   id="color"
                                   name="color"
                                   className="form-control form-control-color"
                                   value={values.color}
                                   onChange={handleColor}
                                   title="Choose your color"/>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <div className="w-100 d-flex justify-content-between align-items-center flex-row-reverse">
                            <Button type="submit" color="success" disabled={!isValid || isPendingSave}>
                                {isPendingSave ? "Saving..." : "Save"}
                            </Button>

                            {isEditModal && (
                                <Button color="danger" onClick={handleRemoveClick} disabled={isPendingDelete}>
                                    {isPendingDelete ? "Removing..." : sureToRemove ? "Confirm" : "Remove Group"}
                                </Button>
                            )}
                        </div>
                    </ModalFooter>
                </form>)}
        </Modal>
    )
}

export default ModalErrorClassifierGroup